import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Ruler, Shirt, Truck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { useCart } from "@/context/CartContext";
import { api, fileUrl, brl } from "@/lib/api";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSite();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [activePhoto, setActivePhoto] = useState(0);
  const [cartOpen, setCartOpen] = useState(false);
  const campaignOpen = settings?.campaign_open;

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      document.title = `${r.data.name} | Loja do Evento`;
    }).catch(() => toast.error("Produto não encontrado"));
  }, [id]);

  if (!product) {
    return (
      <div className="App"><Header /><div className="max-w-7xl mx-auto px-4 py-24 text-center text-[var(--text-muted)]">Carregando...</div></div>
    );
  }

  const availableSizes = (product.sizes || []).filter((s) => s.available);

  const add = () => {
    if (availableSizes.length > 0 && !size) {
      toast.error("Selecione um tamanho.");
      return;
    }
    addItem(product, size || "único", qty);
    toast.success("Adicionado ao carrinho!");
    setCartOpen(true);
  };

  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/pagode-das-cinco-pontas" className="inline-flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] mb-6" data-testid="back-to-store">
          <ArrowLeft size={16} /> Voltar à loja
        </Link>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Photos */}
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden bg-[var(--bg-secondary)] border border-[var(--border)]">
              {product.photos?.[activePhoto] ? (
                <img src={fileUrl(product.photos[activePhoto])} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[var(--text-muted)]">Sem foto</div>
              )}
            </div>
            {product.photos?.length > 1 && (
              <div className="flex gap-3 mt-4">
                {product.photos.map((ph, i) => (
                  <button key={i} onClick={() => setActivePhoto(i)} data-testid={`product-thumb-${i}`}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${activePhoto === i ? "border-[var(--accent)]" : "border-[var(--border)]"}`}>
                    <img src={fileUrl(ph)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <h1 className="font-head text-3xl sm:text-4xl font-black">{product.name}</h1>
            <div className="text-3xl font-black text-[var(--accent)] mt-3">{brl(product.price)}</div>
            {product.description && <p className="text-[var(--text-muted)] mt-4 whitespace-pre-line">{product.description}</p>}

            {!campaignOpen && (
              <div className="mt-6 rounded-xl p-4 text-sm font-semibold" style={{ background: "#2D1216", color: "#EF4444" }} data-testid="product-ended-notice">
                As vendas deste evento estão encerradas.
              </div>
            )}

            {/* Sizes */}
            {availableSizes.length > 0 && (
              <div className="mt-6">
                <div className="text-sm font-semibold mb-2">Tamanho <span className="text-red-400">*</span></div>
                <div className="flex flex-wrap gap-2">
                  {availableSizes.map((s) => (
                    <button key={s.label} onClick={() => setSize(s.label)} data-testid={`size-${s.label}`}
                      className={`min-w-[52px] px-3 py-2 rounded-lg font-bold border-2 transition-all ${
                        size === s.label ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text-main)]"
                      }`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mt-6">
              <div className="text-sm font-semibold mb-2">Quantidade</div>
              <div className="flex items-center gap-3">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-10 rounded-lg border border-[var(--border)]" data-testid="qty-dec">−</button>
                <span className="w-10 text-center font-bold">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="w-10 h-10 rounded-lg border border-[var(--border)]" data-testid="qty-inc">+</button>
              </div>
            </div>

            <Button onClick={add} disabled={!campaignOpen} data-testid="add-to-cart-button" className="w-full btn-primary font-bold h-12 mt-8 disabled:opacity-40">
              {campaignOpen ? "Adicionar ao carrinho" : "Vendas encerradas"}
            </Button>

            {/* Extra info */}
            <div className="mt-8 space-y-4">
              {product.measurements && (
                <Detail icon={Ruler} title="Tabela de medidas" text={product.measurements} />
              )}
              {(product.fabric || product.fit) && (
                <Detail icon={Shirt} title="Tecido e modelagem" text={[product.fabric, product.fit].filter(Boolean).join(" · ")} />
              )}
              {product.delivery_note && (
                <Detail icon={Truck} title="Entrega / retirada" text={product.delivery_note} />
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer brand="stamp" />
      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
    </div>
  );
}

function Detail({ icon: Icon, title, text }) {
  return (
    <div className="card-surface p-5">
      <div className="flex items-center gap-2 font-semibold"><Icon size={16} className="text-[var(--accent)]" /> {title}</div>
      <p className="text-[var(--text-muted)] text-sm mt-2 whitespace-pre-line">{text}</p>
    </div>
  );
}
